import type { SiteListItem } from "@visioni/a3s-provisioning/client-messages";
import type { TenantScenarioStatusItem } from "@visioni/a3s-traffic-flow/fleet-messages";
import { AssetCameraKey, AssetId, CameraId } from "@visioni/a3s-schema";
import { formatCameraLabel } from "@src/utils/key-parser";

/**
 * A scenario is "configured" only when it has BOTH a coordinate (pin) AND a
 * direction (bearing). Missing either — including a coordinate with no bearing,
 * which is the common half-configured case in prod — counts as unconfigured.
 * This is the operator's core status distinction (see the config-redesign design
 * doc §1), so it is defined once here and reused everywhere the worklist derives
 * counts or row status.
 */
export type ScenarioStatus = "configured" | "missing";

export interface WorklistScenarioRow {
  /** Stable list key: `${assetCameraKey}:${scenarioUuid}`. */
  key: string;
  scenarioUuid: string;
  /** Friendly-name override if set, else the backend scenario name. */
  scenarioName: string;
  status: ScenarioStatus;
  /** True when the camera stopped reporting this scenario (soft-deleted). */
  stale?: boolean;
  /** True when the operator excluded this scenario from the roadsense report. */
  excluded?: boolean;
  /** Human label for the asset/camera, e.g. "11287 - Camera A". */
  assetCameraLabel: string;
  /** Raw composite key, needed by the configure action / config page. */
  assetCameraKey: string;
  siteId: string;
}

export interface WorklistSiteGroup {
  siteId: string;
  siteName: string;
  /** Street address if the directory knows it, else undefined. */
  address?: string;
  rows: WorklistScenarioRow[];
  configuredCount: number;
  missingCount: number;
  totalCount: number;
}

export interface WorklistCounts {
  configured: number;
  missing: number;
  total: number;
}

export interface Worklist {
  groups: WorklistSiteGroup[];
  counts: WorklistCounts;
}

export function scenarioStatus(scenario: { coordinate?: unknown; direction?: number }): ScenarioStatus {
  return scenario.coordinate != null && scenario.direction != null ? "configured" : "missing";
}

function assetCameraLabelFor(asset: AssetId, camera: CameraId): string {
  const label = formatCameraLabel(AssetCameraKey.toString(AssetCameraKey.fromParts(asset, camera)));
  // formatCameraLabel returns "" for legacy-app assets it can't render; fall back
  // to the raw composite so the operator still has something to identify the row.
  return label !== "" ? label : `${AssetId.toString(asset)}@${CameraId.toString(camera)}`;
}

/**
 * Builds the operator worklist from the tenant's scenario-status list, grouped by
 * site. Site display context (name + address) comes from the provisioning sites
 * list — NOT the camera list — so the worklist has no camera-fetch dependency
 * (config-redesign design doc: "sites_plus_raw_keys"). Sites the directory
 * doesn't know about fall back to the raw site id as the name.
 *
 * Group order follows first appearance in `items` (stable, matches the backend's
 * own ordering); sorting/filtering for display is a presentation concern layered
 * on top of this pure structure.
 */
export function buildWorklist(items: TenantScenarioStatusItem[], sites: SiteListItem[] = []): Worklist {
  const siteMeta = new Map<string, { name: string; address?: string }>();
  for (const site of sites) {
    siteMeta.set(String(site.siteId), {
      name: site.siteName,
      address: site.address ?? undefined,
    });
  }

  const groups = new Map<string, WorklistSiteGroup>();
  const ordered: WorklistSiteGroup[] = [];

  for (const item of items) {
    const siteId = String(item.site);
    let group = groups.get(siteId);
    if (!group) {
      const meta = siteMeta.get(siteId);
      group = {
        siteId,
        siteName: meta?.name ?? siteId,
        address: meta?.address,
        rows: [],
        configuredCount: 0,
        missingCount: 0,
        totalCount: 0,
      };
      groups.set(siteId, group);
      ordered.push(group);
    }

    const assetCameraKey = AssetCameraKey.toString(AssetCameraKey.fromParts(item.asset, item.camera));
    const assetCameraLabel = assetCameraLabelFor(item.asset, item.camera);

    for (const scenario of item.scenarios) {
      const status = scenarioStatus(scenario);
      const friendly = scenario.friendlyName;
      group.rows.push({
        key: `${assetCameraKey}:${scenario.scenarioUuid}`,
        scenarioUuid: scenario.scenarioUuid,
        scenarioName: friendly && friendly.trim() !== "" ? friendly : scenario.scenarioName,
        status,
        ...(scenario.stale && { stale: true }),
        ...(scenario.excluded && { excluded: true }),
        assetCameraLabel,
        assetCameraKey,
        siteId,
      });
      group.totalCount += 1;
      if (status === "configured") group.configuredCount += 1;
      else group.missingCount += 1;
    }
  }

  const counts = ordered.reduce<WorklistCounts>(
    (acc, group) => ({
      configured: acc.configured + group.configuredCount,
      missing: acc.missing + group.missingCount,
      total: acc.total + group.totalCount,
    }),
    { configured: 0, missing: 0, total: 0 },
  );

  return { groups: ordered, counts };
}
